/**
 * Find a fact-check claim inside text — the pure half of "Show in
 * article" (see lib/claimHighlight.js for the DOM half).
 *
 * The text arrives as SEGMENTS: `{ text }` for a run of text (one DOM
 * text node, or a whole social post) and `{ sep: true }` for a block
 * boundary (a paragraph edge). Matching runs on a normalised copy —
 * whitespace collapsed, curly quotes straightened — with a map from
 * every normalised character back to [segmentIndex, offset], so the
 * caller can build a DOM Range (or split a string) at the exact spot.
 * Same normalisation as backend/fact_check_fixes.py, so a passage the
 * backend could place is found here too.
 *
 * A match never crosses a block boundary.
 *
 * Pure and framework-free — node --test frontend/scripts/claimLocator.test.mjs
 */

const SEP = "\u0000";

const QUOTES = {
  "‘": "'", "’": "'", "‚": "'", "‛": "'", "′": "'",
  "“": '"', "”": '"', "„": '"', "‟": '"', "″": '"',
  "«": '"', "»": '"',
};

function normChar(ch) {
  if (ch === " " || /\s/.test(ch)) return " ";
  return QUOTES[ch] || ch;
}

/** Normalise segments. Returns { text, map } with map[i] = [seg, offset]. */
export function normalizeSegments(segs) {
  const out = [];
  const map = [];
  const last = () => out[out.length - 1];
  (segs || []).forEach((seg, s) => {
    if (!seg) return;
    if (seg.sep) {
      if (last() === " ") {
        out.pop();
        map.pop();
      }
      if (out.length && last() !== SEP) {
        out.push(SEP);
        map.push([s, 0]);
      }
      return;
    }
    const t = seg.text || "";
    for (let k = 0; k < t.length; k++) {
      const c = normChar(t[k]);
      if (c === " " && (!out.length || last() === " " || last() === SEP)) continue;
      out.push(c);
      map.push([s, k]);
    }
  });
  while (out.length && (last() === " " || last() === SEP)) {
    out.pop();
    map.pop();
  }
  return { text: out.join(""), map };
}

/** Normalise a search string (a claim or an excerpt). */
export function normalizeNeedle(str) {
  return normalizeSegments([{ text: String(str || "").trim() }]).text;
}

function findAll(hay, needle) {
  const starts = [];
  if (!needle) return starts;
  let i = hay.indexOf(needle);
  while (i >= 0) {
    starts.push(i);
    i = hay.indexOf(needle, i + needle.length);
  }
  return starts;
}

// Lower-casing char by char keeps indices aligned with the map
// ("İ".toLowerCase() is two chars; taking [0] keeps it one).
function lowerAligned(s) {
  let o = "";
  for (const ch of s) o += ch.toLowerCase()[0] || ch;
  return o;
}

// Variants tried in order, most exact first. Models often wrap a claim
// in quotes or end it with punctuation the article doesn't have.
function variants(needle) {
  const v = [needle];
  const stripped = needle
    .replace(/^["'\s]+|["'\s]+$/g, "")
    .replace(/[.,;:!?]+$/, "")
    .trim();
  if (stripped && stripped !== needle) v.push(stripped);
  return v;
}

/**
 * Locate the first candidate that appears in the segments.
 *
 * `candidates` is tried in order (e.g. [claim.original, claim.claim]);
 * blank ones are skipped. Each is tried exact, then case-insensitive,
 * then without wrapping quotes / trailing punctuation.
 *
 * Returns { matches: [{ start: [seg, off], end: [seg, off] }], needle }
 * where `end` is EXCLUSIVE (offset one past the last char), or null.
 * Every occurrence is returned, in document order.
 */
export function locate(segs, candidates) {
  const { text, map } = normalizeSegments(segs);
  if (!text) return null;
  const lowered = lowerAligned(text);
  for (const raw of candidates || []) {
    const base = normalizeNeedle(raw);
    if (!base || base.length < 2) continue;
    for (const needle of variants(base)) {
      for (const [hay, n] of [[text, needle], [lowered, lowerAligned(needle)]]) {
        const starts = findAll(hay, n);
        if (starts.length) {
          return {
            needle,
            matches: starts.map((i) => {
              const a = map[i];
              const b = map[i + n.length - 1];
              return { start: [a[0], a[1]], end: [b[0], b[1] + 1] };
            }),
          };
        }
      }
    }
  }
  return null;
}

/**
 * Split one plain string into parts for rendering with <mark>:
 * [{ text, hit: bool }]. Used for social posts, which aren't in the DOM
 * as article text. Returns null when nothing matches.
 */
export function splitForHighlight(text, candidates) {
  const src = String(text || "");
  const found = locate([{ text: src }], candidates);
  if (!found) return null;
  const parts = [];
  let pos = 0;
  for (const m of found.matches) {
    const a = m.start[1];
    const b = m.end[1];
    if (a > pos) parts.push({ text: src.slice(pos, a), hit: false });
    parts.push({ text: src.slice(a, b), hit: true });
    pos = b;
  }
  if (pos < src.length) parts.push({ text: src.slice(pos), hit: false });
  return parts;
}

/**
 * The strings to search for a claim, most precise first. Once a fix is
 * applied the original passage is gone, so look for the new text.
 */
export function claimCandidates(claim) {
  if (!claim) return [];
  const list = claim.fix_status === "applied"
    ? [claim.proposed, claim.claim]
    : [claim.original, claim.claim];
  return list.filter((s) => (s || "").trim());
}